import { useState, useEffect } from 'react';
import api from '../api/axios';
import PageTitle from '../components/PageTitle';

export default function NewsArticlesFiles() {
  const [activeTab, setActiveTab] = useState('news'); // 'news', 'articles', 'files'
  const [news, setNews] = useState([]);
  const [articles, setArticles] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/api/news/public').then(r => setNews(r.data)).catch(e => console.error(e)),
      api.get('/api/articles/public').then(r => setArticles(r.data)).catch(e => console.error(e)),
      api.get('/api/admin-files/public').then(r => setFiles(r.data)).catch(e => console.error(e))
    ]).finally(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <>
      <PageTitle title="Resources & Updates" />
      
      <div className="page-hero">
        <div className="page-hero-label">Tax Intelligence</div>
        <h1>Updates & Resources</h1>
        <p>Stay updated with the latest in direct tax, GST, and downloadable utilities.</p>
      </div>

      <div className="page-wide">
        {/* Tab switcher */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          borderBottom: '2px solid var(--creamd)',
          marginBottom: '2rem',
          paddingBottom: '0.5rem'
        }}>
          {['news', 'articles', 'files'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'none',
                border: 'none',
                padding: '0.75rem 1.25rem',
                fontSize: '1rem',
                fontWeight: 600,
                color: activeTab === tab ? 'var(--navy)' : 'var(--muted)',
                borderBottom: activeTab === tab ? '3px solid var(--gold)' : '3px solid transparent',
                cursor: 'pointer',
                textTransform: 'capitalize',
                transition: 'all 0.2s'
              }}
            >
              {tab === 'news' ? '📢 Latest News' : tab === 'articles' ? '✍️ Articles' : '📁 Downloads & Files'}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner spinner-dark"></div>
            <p style={{ marginTop: '1rem', color: 'var(--muted)' }}>Loading resources...</p>
          </div>
        ) : (
          <div>
            {/* 📢 NEWS TAB */}
            {activeTab === 'news' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {news.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>
                    No tax news published yet. Check back later!
                  </div>
                ) : (
                  news.map(item => (
                    <div key={item.id} className="card" style={{ borderLeft: '4px solid var(--gold)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <h3 style={{ fontFamily: 'Playfair Display, serif', color: 'var(--navy)', fontSize: '1.25rem' }}>{item.title}</h3>
                        <span style={{ fontSize: '0.78rem', color: 'var(--muted)', fontWeight: 500 }}>
                          Published: {formatDate(item.publishedAt)}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.92rem', color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {item.content}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ✍️ ARTICLES TAB */}
            {activeTab === 'articles' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {articles.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>
                    No articles published yet. Check back later!
                  </div>
                ) : (
                  articles.map(item => (
                    <div key={item.id} className="card">
                      <h3 style={{ fontFamily: 'Playfair Display, serif', color: 'var(--navy)', fontSize: '1.4rem', marginBottom: '0.5rem' }}>{item.title}</h3>
                      <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.25rem', fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 500 }}>
                        <span>👤 Author: {item.author}</span>
                        <span>📅 Published: {formatDate(item.publishedAt)}</span>
                      </div>
                      <p style={{ fontSize: '0.94rem', color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                        {item.content}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* 📁 DOWNLOADS TAB */}
            {activeTab === 'files' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.25rem' }}>
                {files.length === 0 ? (
                  <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>
                    No utilities or files uploaded yet. Check back later!
                  </div>
                ) : (
                  files.map(file => (
                    <div key={file.id} className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📄</div>
                        <h3 style={{ fontFamily: 'Playfair Display, serif', color: 'var(--navy)', fontSize: '1.15rem', marginBottom: '0.5rem' }}>{file.title}</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '1.25rem', lineHeight: 1.5 }}>{file.description}</p>
                      </div>
                      <div style={{ borderTop: '1px solid var(--creamd)', paddingTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
                          Uploaded: {formatDate(file.uploadedAt)}
                        </span>
                        <a 
                          href={`/api/admin-files/download/${file.fileName}`}
                          download
                          className="btn btn-gold btn-sm"
                          style={{ textDecoration: 'none' }}
                        >
                          📥 Download
                        </a>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
